import { Shield, Compass, Sprout } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";

export default function About() {
  const { t } = useLanguage();
  usePageTitle(t.meta.about);
  const valueIcons = [Shield, Compass, Sprout];

  return (
    <div data-testid="about-page" className="pt-24">
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-16 md:px-10 md:pt-24">
        <Reveal>
          <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
            <span className="h-px w-10 bg-bronze" />
            {t.about.overline}
          </p>
          <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[0.95] tracking-tight text-ink md:text-6xl lg:text-7xl">
            {t.about.title}
          </h1>
        </Reveal>
        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="font-serif text-2xl leading-snug text-ink md:text-3xl">{t.about.lead}</p>
            <p className="mt-8 text-base leading-relaxed text-stone md:text-lg">{t.about.p1}</p>
            <p className="mt-6 text-base leading-relaxed text-stone md:text-lg">{t.about.p2}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="relative overflow-hidden border border-line">
              <img
                src="https://images.unsplash.com/photo-1574943320219-553eb213f72d?q=80&w=1200&auto=format&fit=crop"
                alt="Mâini cu pământ și răsaduri"
                className="h-full max-h-[520px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-line bg-sand/30">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">{t.about.valuesOverline}</p>
          </Reveal>
          <div className="mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
            {t.manifesto.chapters.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={v.title} delay={i * 0.1}>
                  <div className="group border border-line bg-sand p-8 transition-colors duration-500 hover:border-bronze/40">
                    <span className="flex h-12 w-12 items-center justify-center border border-bronze/30 bg-ivory">
                      <Icon className="h-5 w-5 text-bronze" />
                    </span>
                    <h3 className="mt-6 font-serif text-3xl text-ink">{v.title}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-stone">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-ink px-6 py-24 text-center md:py-36">
        <Reveal>
          <blockquote data-testid="about-vision-quote" className="mx-auto max-w-4xl font-serif text-3xl italic leading-snug text-ivory md:text-5xl">
            „{t.about.quote}"
          </blockquote>
          <p className="mt-8 text-xs uppercase tracking-[0.3em] text-bronze">{t.about.quoteBy}</p>
        </Reveal>
      </section>

      <CTASection />
    </div>
  );
}
